import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
	VIDEO_ELEMENT,
	REFLECTION_ELEMENT,
	SURVEY_ELEMENT,
	LINK_ELEMENT,
	IMAGE_ELEMENT,
	AUDIO_ELEMENT,
	DOWNLOAD_ELEMENT,
	TEXT_ELEMENT,
	DOCUMENT_ELEMENT,
	SIGNATURE_ELEMENT,
	ROLE_STUDENT,
	RATING_ELEMENT,
	QUOTES_ELEMENT,
	CUSTOM_PLAN_ELEMENT,
	ROLE_EMPLOYEE,
	LIBRARY_TYPE_TRAINING,
	LIBRARY_TYPE_IN_PERSON_TRAINING,
	LIBRARY_TYPE_WEBINAR,
	LIBRARY_TYPE_INTELLIGUIDE,
	LIBRARY_TYPE_LEARNING_HABIT,
	SIDEBAR_LIBRARY_LESSON_DETAILS_PAGE,
	SEARCH_TIMER_DELAY,
	LIBRARY_TYPE_SOP,
} from "../models/Constants";

import { ROLE_ADMIN, ROLE_MENTOR, ROLE_OWNER } from "../models/Constants";
import { isEmpty } from "./Functions";
import { useLocation } from "react-router-dom";
import { useMatch, useNavigate } from "react-router-dom";
import {
	URL_LIBRARY_IN_PERSON_TRAINING,
	URL_LIBRARY_INTELLIGUIDE,
	URL_LIBRARY_LEARNING_HABITS,
	URL_LIBRARY_SOP,
	URL_LIBRARY_TRAINING,
	URL_LIBRARY_WEBINARS,
} from "../app/dashboard/DashboardRoutes";

export const getRole = (roleID) => {
	switch (roleID) {
		case 1:
			return "Admin";
		case 2:
			return "Student";
		case 3:
			return "Mentor";
		case 4:
			return "Otoma Admin";
		case 5:
			return "Employee";
		case 6:
			return "Owner";
		default:
			return "Custom";
	}
};

export const roleNameToStandardRoleId = (roleName) => {
	switch (roleName?.toLowerCase()) {
		case "student":
			return ROLE_STUDENT;
		case "admin":
			return ROLE_ADMIN;
		case "mentor":
			return ROLE_MENTOR;
		case "owner":
			return ROLE_OWNER;
		case "employee":
			return ROLE_EMPLOYEE;
		default:
			return null;
	}
};

export const getCompanyCapabilities = (userCapabilities, companyID) => {
	let selected;

	for (const obj of userCapabilities) {
		if (obj.company_id === companyID) {
			selected = obj;
			break;
		}
	}

	return selected;
};

export const getElementType = (elementTypeID) => {
	switch (elementTypeID) {
		case VIDEO_ELEMENT:
			return "Video";
		case REFLECTION_ELEMENT:
			return "Reflection";
		case SURVEY_ELEMENT:
			return "Survey";
		case LINK_ELEMENT:
			return "Link";
		case IMAGE_ELEMENT:
			return "Image";
		case AUDIO_ELEMENT:
			return "Audio";
		case DOWNLOAD_ELEMENT:
			return "Download";
		case TEXT_ELEMENT:
			return "Text";
		case DOCUMENT_ELEMENT:
			return "Document";
		case SIGNATURE_ELEMENT:
			return "Signature";
		case RATING_ELEMENT:
			return "Ratings";
		case QUOTES_ELEMENT:
			return "Quote";
		case CUSTOM_PLAN_ELEMENT:
			return "Custom Plan";
	}
};

export const isNotStudent = (role) => {
	if (isEmpty(role)) {
		return false;
	}

	return role != ROLE_STUDENT;
};

export const useLoading = () => {
	const [loadingCount, setLoadingCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setIsLoading(loadingCount > 0);
	}, [loadingCount]);

	const addLoading = () => setLoadingCount((prev) => prev + 1);
	const finishLoading = () => setLoadingCount((prev) => Math.max(0, prev - 1));

	return {
		isLoading,
		setIsLoading,
		loadingCount,
		addLoading,
		finishLoading,
	};
};

export const useMultiLoading = () => {
	const [loadingCounts, setLoadingCounts] = useState({});
	const [globalLoading, setGlobalLoading] = useState(false);

	useEffect(() => {
		// Global loading state should be true if any key has a count > 0
		setGlobalLoading(Object.values(loadingCounts).some((count) => count > 0));
	}, [loadingCounts]);

	const addLoading = (key = null) => {
		setLoadingCounts((prev) => {
			if (key) {
				return { ...prev, [key]: (prev[key] || 0) + 1 };
			}
			return { ...prev, global: (prev.global || 0) + 1 };
		});
	};

	const finishLoading = (key = "global", weight = 1) => {
		setLoadingCounts((prev) => {
			if (key) {
				if (weight < 0) {
					// wipe all loading count
					const updatedState = { ...prev };
					delete updatedState[key];
					return updatedState;
				}

				return { ...prev, [key]: Math.max(0, (prev[key] || 0) - weight) };
			}
		});
	};

	const isLoading = (key = null) => {
		if (key) {
			if (!loadingCounts[key]) {
				return false;
			}

			return (loadingCounts[key] || 0) > 0;
		}
		return globalLoading;
	};

	return {
		isLoading,
		addLoading,
		finishLoading,
		loadingCounts, // Useful for debugging if needed
	};
};

export const useForceRender = () => {
	const [, setToggle] = useState(false);

	// Function to force a render
	const forceRender = useCallback(() => {
		setToggle((prev) => !prev);
	}, []);

	return forceRender;
};

export const useFirstMount = () => {
	const isFirstMount = useRef(true);

	useEffect(() => {
		isFirstMount.current = false;
	}, []);

	return isFirstMount.current;
};

export const useCompanyUrl = () => {
	const [isTellix, setIsTellix] = useState(false);

	const location = useLocation();
	let baseUrl = process.env.REACT_APP_TELLIX_URL ?? "";

	if (!isEmpty(baseUrl)) {
		baseUrl = new URL(baseUrl).origin;
	}

	useEffect(() => {
		setIsTellix(window.location.origin === baseUrl);
	}, [location]);

	return {
		isTellix,
	};
};

export const convertLibraryType = (value) => {
	const mapping = {
		[LIBRARY_TYPE_TRAINING]: URL_LIBRARY_TRAINING,
		[LIBRARY_TYPE_IN_PERSON_TRAINING]: URL_LIBRARY_IN_PERSON_TRAINING,
		[LIBRARY_TYPE_WEBINAR]: URL_LIBRARY_WEBINARS,
		[LIBRARY_TYPE_INTELLIGUIDE]: URL_LIBRARY_INTELLIGUIDE,
		[LIBRARY_TYPE_SOP]: URL_LIBRARY_SOP,
		[LIBRARY_TYPE_LEARNING_HABIT]: URL_LIBRARY_LEARNING_HABITS,
	};

	// everything that is a number is a library type and be converted to a library type name/url
	let toBaseUrl = !Number.isNaN(parseInt(value));

	if (toBaseUrl) {
		return mapping[value] || null;
	}

	const reverseMapping = Object.fromEntries(Object.entries(mapping).map(([k, v]) => [v, k]));

	return parseInt(reverseMapping[value]) || null;
};

export const useLibraryRoutesUtils = () => {
	const libraryPageLocation = useMatch("/dashboard/:type/:library_code");
	const navigate = useNavigate();

	const libraryType = libraryPageLocation?.params?.type;
	const libraryCode = libraryPageLocation?.params?.library_code;
	const libraryTypeId = useMemo(() => {
		return convertLibraryType(libraryType);
	}, [libraryType]);

	const gotoLibrary = (libraryCode, libraryType = null, params = {}) => {
		let libraryBaseTypeUrl;

		if (!isEmpty(libraryType)) {
			libraryBaseTypeUrl = convertLibraryType(libraryType);
		}
		// if no library type is provided and the user is already on a valid library dashboard route, use the current route's base type.
		else if (!isEmpty(libraryType)) {
			libraryBaseTypeUrl = libraryType;
		}

		if (!isEmpty(libraryBaseTypeUrl)) {
			const isLearningHabitBaseUrl = libraryBaseTypeUrl === URL_LIBRARY_LEARNING_HABITS;

			if (isLearningHabitBaseUrl) {
				libraryBaseTypeUrl = libraryBaseTypeUrl + "/lessons";
			}

			let libraryUrl = `/dashboard/${libraryBaseTypeUrl}/${libraryCode}`;

			const searchParams = new URLSearchParams(params).toString();
			if (searchParams) {
				libraryUrl += `?${searchParams}`;
			}

			if (isLearningHabitBaseUrl) {
				libraryUrl += `#${SIDEBAR_LIBRARY_LESSON_DETAILS_PAGE}`; // direcly go to lesson page
			}

			navigate(libraryUrl, { replace: true });
			return true;
		}

		return false;
	};

	const quickTestAssistant = (library) => {
		if (isEmpty(library)) {
			return;
		}

		const assistant = {
			code: library?.code,
			assistant_details: {
				...library?.ai_assistant,
				library_id: library?.party_id,
			},
			context: library?.context,
			description: library?.description,
			image_file: library?.image_file,
			party_id: library?.party_id,
			title: library?.title,
			is_sop: library?.library_type_id === LIBRARY_TYPE_SOP,
		};

		navigate('/dashboard/ai-chat/', { state: { assistant }, replace: true });
	}

	return {
		libraryTypeBaseUrl: libraryTypeId ? libraryType : null,
		libraryTypeId,
		libraryCode: libraryTypeId ? libraryCode : null,
		gotoLibrary,
		quickTestAssistant,
	};
};

/**
 * Truncate text to a word limit, adding "..." if exceeded.
 * Handles long single words by truncating them as well.
 * @param {string} text - The input text to truncate.
 * @param {number} wordLimit - Maximum number of words allowed.
 * @param {number} [maxWordLength=30] - Maximum allowed length for a single word before truncating it.
 * @returns {string} - Truncated text with ellipsis if needed.
 */
export function truncateTextWithEllipsis(text, wordLimit, maxWordLength = 30) {
	if (!text || typeof text !== "string" || wordLimit <= 0) return "";

	const words = text.split(/\s+/);

	// Handle long single word spam
	const processedWords = words.map((word) =>
		word.length > maxWordLength ? word.slice(0, maxWordLength) + "..." : word
	);

	if (processedWords.length <= wordLimit) {
		return processedWords.join(" ");
	}

	const truncated = processedWords.slice(0, wordLimit).join(" ");
	return truncated + "...";
}

export function useSearchDelay(typeDelay = SEARCH_TIMER_DELAY) {
	const [searchValue, setSearchValue] = useState("");
	const [toSearchValue, setToSearchValue] = useState("");

	const searchDelayTimerRef = useRef(null);

	const clearSearchDelay = () => {
		clearTimeout(searchDelayTimerRef.current);
		searchDelayTimerRef.current = null;
	};

	useEffect(() => {
		if (!isEmpty(searchValue)) {
			clearTimeout(searchDelayTimerRef.current);
			searchDelayTimerRef.current = setTimeout(() => {
				setToSearchValue(searchValue);
			}, typeDelay);
		} else {
			setToSearchValue("");
		}

		return () => {
			clearSearchDelay();
		};
	}, [searchValue]);

	return {
		searchValue,
		toSearchValue,
		setSearchValue,
		clearSearchDelay,
	};
}

export function libraryTypeToLink(code = null, libraryType = LIBRARY_TYPE_TRAINING) {
	switch (libraryType) {
		case LIBRARY_TYPE_IN_PERSON_TRAINING:
			return `/dashboard/in-person-trainings/${code}`;
		case LIBRARY_TYPE_WEBINAR:
			return `/dashboard/webinars/${code}`;
		case LIBRARY_TYPE_INTELLIGUIDE:
			return `/dashboard/company_apps/${code}`;
		case LIBRARY_TYPE_SOP:
			return `/dashboard/sops/${code}`;
		case LIBRARY_TYPE_LEARNING_HABIT:
			return `/dashboard/learning-habits/lessons/${code}#${SIDEBAR_LIBRARY_LESSON_DETAILS_PAGE}`;
		default:
			return `/dashboard/trainings/${code}`;
	}
}

export function useInfiniteScroll(containerRef, offsetPercent = 0.8, debounceDelay = 200, position = "bottom") {
	const [isNearBottom, setIsNearBottom] = useState(false);
	const [scrollPosition, setScrollPosition] = useState(0);
	const [scrollPercent, setScrollPercent] = useState(0);
	const debounceTimerRef = useRef(null);

	useEffect(() => {
		const container = containerRef?.current;
		if (!container) return;

		const handleScroll = () => {
			clearTimeout(debounceTimerRef.current);
			
			debounceTimerRef.current = setTimeout(() => {
				const { scrollTop, scrollHeight, clientHeight } = container;
				
				// Calculate scroll position and percentage
				const currentPosition = scrollTop;
				const maxScroll = scrollHeight - clientHeight;
				const percent = maxScroll > 0 ? scrollTop / maxScroll : 0;

				setScrollPosition(currentPosition);
				setScrollPercent(percent);

				// Check if near threshold based on position
				if (position === "bottom") {
					const isNear = percent >= offsetPercent;
					setIsNearBottom(isNear);
				} else if (position === "top") {
					const isNear = percent <= (1 - offsetPercent);
					setIsNearBottom(isNear);
				}
			}, debounceDelay);
		};

		// Initial check
		handleScroll();

		container.addEventListener("scroll", handleScroll);
		
		return () => {
			container.removeEventListener("scroll", handleScroll);
			clearTimeout(debounceTimerRef.current);
		};
	}, [containerRef, offsetPercent, debounceDelay, position]);

	return {
		isNearBottom,
		scrollPosition,
		scrollPercent,
	};
}

export function usePaginateList(appendLast = true, idField = 'id') {
    const loadedPagesRef = useRef(new Set());
    const pageDataCache = useRef({});
    const nextPageRef = useRef(1);
    const totalPagesRef = useRef(null);

    // Add new set of items - returns merged array
    const addItems = useCallback((currentItems = [], newItems = [], page = 1) => {
        if (!newItems || newItems.length === 0) return currentItems;

        // Mark page as loaded
        loadedPagesRef.current.add(page);
        pageDataCache.current[page] = newItems;

        // Update next page to be one more than the highest loaded page
        const allPages = Array.from(loadedPagesRef.current);
        const maxPage = Math.max(...allPages);
        const calculatedNextPage = maxPage + 1;
        
        // Don't exceed total pages if set
        if (totalPagesRef.current !== null) {
            nextPageRef.current = Math.min(calculatedNextPage, totalPagesRef.current);
        } else {
            nextPageRef.current = calculatedNextPage;
        }

        // Create a Set of existing item IDs
        const existingIds = new Set(
            currentItems.map((item) => item[idField])
        );

        // Filter duplicates
        const uniqueNewItems = newItems.filter((item) => 
            !existingIds.has(item[idField])
        );

        // Return merged array
        return appendLast 
            ? [...currentItems, ...uniqueNewItems]
            : [...uniqueNewItems, ...currentItems];
    }, [appendLast, idField]);

    const setTotalPages = useCallback((total) => {
        totalPagesRef.current = total;
        // Adjust nextPage if it exceeds the new total
        if (total !== null && nextPageRef.current > total) {
            nextPageRef.current = total;
        }
    }, []);

    const clearCache = useCallback(() => {
        loadedPagesRef.current.clear();
        pageDataCache.current = {};
        nextPageRef.current = 1;
        totalPagesRef.current = null;
    }, []);

    const isPageLoaded = useCallback((page) => {
        return loadedPagesRef.current.has(page);
    }, []);

    const getPageData = useCallback((page) => {
        return pageDataCache.current[page] || [];
    }, []);

    const hasMorePages = useCallback(() => {
        if (totalPagesRef.current === null) return true;
        return nextPageRef.current <= totalPagesRef.current;
    }, []);

    return {
        addItems,
        clearCache,
        isPageLoaded,
        getPageData,
        setTotalPages,
        loadedPages: Array.from(loadedPagesRef.current),
        nextPage: nextPageRef.current,
        totalPages: totalPagesRef.current,
        hasMorePages: hasMorePages(),
    };
}